/* Panelin iki grafiği — çizgi ve sütun — kütüphanesiz, SVG.

   Bir grafik kütüphanesi (Chart.js 70 KB, d3 parçaları 30 KB) panelin
   kalanından ağır olurdu, ve burada gereken iki şekil var: zamana göre
   gecikme (çizgi) ve saat başına istek (sütun). İkisi de **tek seri**:
   birden çok hedef yan yana gösterilecekse küçük katlar (her biri kendi
   grafiği) kullanılıyor, renkle ayrılmış dört çizgi değil. Böylece kimlik
   hiçbir yerde yalnızca renge kalmıyor ve kategorik palet gerekmiyor.

   Çizim kuralları (dataviz):
     · çizgi 2 px, sütun ucu 4 px yuvarlak ve tabana oturuyor, sütunlar
       arasında 2 px boşluk
     · ızgara silik, eksen yazısı ikincil mürekkep — veri rengi yazıya
       geçmiyor
     · her grafikte üzerine gelince ipucu: çizgide dikey imleç + en yakın
       nokta, sütunda sütunun kendisi (vuruş alanı sütundan geniş)
     · kesinti kırmızı ve her zaman bir etiketle ("kesinti"), yalnız renkle
       değil */

const NS = 'http://www.w3.org/2000/svg';

function svg<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}): SVGElementTagNameMap[K] {
  const node = document.createElementNS(NS, tag);
  for (const [key, value] of Object.entries(attrs)) node.setAttribute(key, String(value));
  return node;
}

const PAD = { top: 10, right: 12, bottom: 22, left: 44 };

/** Okunaklı eksen adımları: 1, 2, 5 × 10ⁿ. */
export function niceMax(value: number): number {
  if (value <= 0) return 1;
  const exp = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 5, 10]) {
    if (value <= step * exp) return step * exp;
  }
  return 10 * exp;
}

function hourLabel(epoch: number): string {
  return new Date(epoch).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * `width` SVG'nin iç koordinat genişliği. Yazı boyutu buna göre ölçekleniyor,
 * yani grafiğin ekranda kaplayacağı genişliğe yakın verilmeli: tam genişlik
 * bir grafiği 520 birimle çizmek eksen yazısını iki katına çıkarıyordu.
 */
function frame(label: string, height: number, width: number): { root: HTMLElement; plot: SVGSVGElement; tip: HTMLElement; width: number } {
  const root = document.createElement('figure');
  root.className = 'chart';
  const plot = svg('svg', { viewBox: `0 0 ${width} ${height}`, role: 'img', 'aria-label': label });
  const tip = document.createElement('div');
  tip.className = 'chart-tip';
  tip.hidden = true;
  root.append(plot, tip);
  return { root, plot, tip, width };
}

function gridAndAxis(plot: SVGSVGElement, width: number, height: number, max: number, unit: string): void {
  const inner = height - PAD.top - PAD.bottom;
  for (const fraction of [0, 0.5, 1]) {
    const y = PAD.top + inner * (1 - fraction);
    plot.append(svg('line', { x1: PAD.left, x2: width - PAD.right, y1: y, y2: y, class: fraction === 0 ? 'axis' : 'grid' }));
    const text = svg('text', { x: PAD.left - 6, y: y + 4, 'text-anchor': 'end', class: 'tick' });
    /* Birim yalnız kısa olduğunda (ms) eksende: "200 istek" sol boşluğa
       sığmıyor, ve uzun birim zaten grafiğin başlığında ve ipucunda. */
    text.textContent = `${Math.round(max * fraction).toLocaleString('tr-TR')}${fraction === 1 && unit.length <= 2 ? ` ${unit}` : ''}`;
    plot.append(text);
  }
}

function placeTip(root: HTMLElement, tip: HTMLElement, plot: SVGSVGElement, x: number, width: number, html: Node[]): void {
  tip.replaceChildren(...html);
  tip.hidden = false;
  const scale = plot.getBoundingClientRect().width / width;
  const left = x * scale;
  const half = tip.offsetWidth / 2;
  tip.style.left = `${Math.min(Math.max(left - half, 0), root.clientWidth - tip.offsetWidth)}px`;
}

function line(text: string, cls = ''): HTMLElement {
  const node = document.createElement('div');
  if (cls) node.className = cls;
  node.textContent = text;
  return node;
}

/* ── Çizgi ──────────────────────────────────────────────── */

export type LinePoint = { at: number; value: number; ok: boolean };

/**
 * Zamana göre tek seri. Başarısız noktalar çizgiye girmiyor (8 saniyelik
 * zaman aşımı ölçeği ezerdi); tabanda kırmızı birer işaret olarak duruyor.
 */
export function lineChart(opts: { label: string; points: LinePoint[]; from: number; to: number; unit: string; height?: number; width?: number }): HTMLElement {
  const height = opts.height ?? 120;
  const { root, plot, tip, width } = frame(opts.label, height, opts.width ?? 520);
  const inner = { w: width - PAD.left - PAD.right, h: height - PAD.top - PAD.bottom };
  const good = opts.points.filter((p) => p.ok);
  const max = niceMax(Math.max(1, ...good.map((p) => p.value)));
  const span = Math.max(1, opts.to - opts.from);
  const xOf = (at: number) => PAD.left + ((at - opts.from) / span) * inner.w;
  const yOf = (v: number) => PAD.top + inner.h * (1 - Math.min(v, max) / max);

  gridAndAxis(plot, width, height, max, opts.unit);
  for (const at of [opts.from, opts.from + span / 2, opts.to]) {
    const text = svg('text', { x: xOf(at), y: height - 6, 'text-anchor': 'middle', class: 'tick' });
    text.textContent = hourLabel(at);
    plot.append(text);
  }

  if (good.length > 0) {
    /* 20 dakikadan uzun boşlukta çizgi kopuyor: kayıt yokken yoklama da
       yoktu, aradaki düz çizgi uydurma bir ölçüm olurdu. */
    let d = '';
    let prev: number | null = null;
    for (const p of good) {
      d += `${prev === null || p.at - prev > 20 * 60_000 ? 'M' : 'L'}${xOf(p.at).toFixed(1)},${yOf(p.value).toFixed(1)}`;
      prev = p.at;
    }
    plot.append(svg('path', { d, class: 'series' }));
  }
  for (const p of opts.points.filter((x) => !x.ok)) {
    plot.append(svg('rect', { x: xOf(p.at) - 1.5, y: PAD.top + inner.h - 8, width: 3, height: 8, class: 'outage' }));
  }

  const cursor = svg('line', { y1: PAD.top, y2: PAD.top + inner.h, class: 'cursor', visibility: 'hidden' });
  const dot = svg('circle', { r: 4, class: 'dot', visibility: 'hidden' });
  const hit = svg('rect', { x: PAD.left, y: 0, width: inner.w, height, fill: 'transparent' });
  plot.append(cursor, dot, hit);

  hit.addEventListener('pointermove', (event) => {
    if (opts.points.length === 0) return;
    const box = plot.getBoundingClientRect();
    const x = ((event.clientX - box.left) / box.width) * width;
    const at = opts.from + ((x - PAD.left) / inner.w) * span;
    const near = opts.points.reduce((a, b) => (Math.abs(b.at - at) < Math.abs(a.at - at) ? b : a));
    const px = xOf(near.at);
    cursor.setAttribute('x1', String(px));
    cursor.setAttribute('x2', String(px));
    cursor.setAttribute('visibility', 'visible');
    if (near.ok) {
      dot.setAttribute('cx', String(px));
      dot.setAttribute('cy', String(yOf(near.value)));
      dot.setAttribute('visibility', 'visible');
    } else {
      dot.setAttribute('visibility', 'hidden');
    }
    placeTip(root, tip, plot, px, width, [
      line(hourLabel(near.at), 'tip-head'),
      near.ok ? line(`${near.value.toLocaleString('tr-TR')} ${opts.unit}`) : line('✕ kesinti', 'bad')
    ]);
  });
  hit.addEventListener('pointerleave', () => {
    cursor.setAttribute('visibility', 'hidden');
    dot.setAttribute('visibility', 'hidden');
    tip.hidden = true;
  });
  return root;
}

/* ── Sütun ──────────────────────────────────────────────── */

export type Bar = { at: number; value: number; detail: string[] };

export function dayLabel(epoch: number): string {
  return new Date(epoch).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

export function barChart(opts: {
  label: string;
  bars: Bar[];
  unit: string;
  height?: number;
  width?: number;
  /** Eksen ve ipucu etiketi; varsayılan saat ("14:00"). Günlük grafikte tarih. */
  format?: (epoch: number) => string;
}): HTMLElement {
  const format = opts.format ?? hourLabel;
  const height = opts.height ?? 140;
  const { root, plot, tip, width } = frame(opts.label, height, opts.width ?? 1040);
  const inner = { w: width - PAD.left - PAD.right, h: height - PAD.top - PAD.bottom };
  const max = niceMax(Math.max(1, ...opts.bars.map((b) => b.value)));
  gridAndAxis(plot, width, height, max, opts.unit);

  const n = Math.max(1, opts.bars.length);
  const slot = inner.w / n;
  const gap = 2;
  const barW = Math.max(1, slot - gap);
  opts.bars.forEach((bar, i) => {
    const x = PAD.left + i * slot + gap / 2;
    const h = (bar.value / max) * inner.h;
    const base = PAD.top + inner.h;
    if (h > 0) {
      /* Yalnız üst köşeler yuvarlak: sütun tabana oturuyor. */
      const r = Math.min(4, barW / 2, h);
      const d = `M${x},${base}V${base - h + r}Q${x},${base - h} ${x + r},${base - h}H${x + barW - r}Q${x + barW},${base - h} ${x + barW},${base - h + r}V${base}Z`;
      plot.append(svg('path', { d, class: 'bar-mark' }));
    }
    if (i % Math.ceil(n / 6) === 0) {
      const text = svg('text', { x: x + barW / 2, y: height - 6, 'text-anchor': 'middle', class: 'tick' });
      text.textContent = format(bar.at);
      plot.append(text);
    }
    /* Vuruş alanı sütunun tamamı ve tam yükseklik: alçak bir sütun da
       kolayca yakalanır. */
    const hit = svg('rect', { x: x - gap / 2, y: PAD.top, width: slot, height: inner.h, fill: 'transparent' });
    hit.addEventListener('pointerenter', () =>
      placeTip(root, tip, plot, x + barW / 2, width, [line(format(bar.at), 'tip-head'), ...bar.detail.map((t) => line(t))])
    );
    hit.addEventListener('pointerleave', () => {
      tip.hidden = true;
    });
    plot.append(hit);
  });
  return root;
}
